/**
 * DealPhaseProgress - Barra de progresso visual baseada na posição da fase
 * Mostra o avanço do deal dentro do pipeline usando cor da fase
 * Versão: 1.0.0
 */
import React from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Props do componente
interface DealPhaseProgressProps {
  /** Nome da fase atual */
  phaseName: string;
  /** Cor da fase (hex ou hsl) */
  phaseColor: string | null;
  /** Posição da fase no pipeline (1-indexed) */
  phaseSortOrder: number;
  /** Total de fases no pipeline */
  totalPhases: number;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Barra de progresso visual da fase no pipeline
 * A barra progride conforme a posição da fase
 */
export function DealPhaseProgress({
  phaseName,
  phaseColor,
  phaseSortOrder,
  totalPhases,
  className
}: DealPhaseProgressProps) {
  // Calcula porcentagem de progresso (mínimo 10% para sempre mostrar algo)
  const progressPercent = totalPhases > 0 
    ? Math.max(10, (phaseSortOrder / totalPhases) * 100)
    : 10;

  // Cor padrão se não houver cor definida
  const barColor = phaseColor || '#6b7280';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("flex items-center gap-2", className)}>
            {/* Barra de progresso */}
            <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden shrink-0">
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${progressPercent}%`,
                  backgroundColor: barColor
                }}
              />
            </div>
            {/* Nome da fase */}
            <span className="text-xs truncate max-w-[120px]">{phaseName}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="font-medium">{phaseName}</p>
          <p className="text-muted-foreground">
            Fase {phaseSortOrder} de {totalPhases}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
